/**
 * @file components/common/FileUpload.js
 * @author chenbo09
 *
 * fileList作为内部变量供acud的Upload使用，formValue对外供Form使用. 该组件是个非受控的。
 */

import React, {useState, useCallback} from 'react';
import _ from 'lodash';
import {Upload, Button} from 'acud';
import {OutlinedUpload} from 'acud-icon';

import {DU_PREFIX} from '../../_common/constants';
import {message} from '../../../utils/bizUtil';

import {useLocale} from '../../locale';

import './index.less';

const CLS_PREFIX = `${DU_PREFIX}-fileupload`;
const DEFAULT_ACTION = '/file/upload';

export interface FileUploadProps {
    action: string;
    value?: any[];
    tipText?: string;
    children?: any;
    beforeUpload?: (file: any, fileList: any[]) => boolean;
    onChange?: any
}

const propToFileList = (value: any) => {
    if (_.isArray(value) && value.length) {
        return value.map(valueItem => {
            const {uid, name, url} = valueItem;
            return {
                uid: uid,
                name,
                status: 'done',
                url
            };
        });
    }
    return [];
};

// 内部数据结构转换为表单需要的数据结构
const fileListToForm = (fileList: any[]) => {
    const formValue: any[] = [];
    fileList.forEach(item => {
        const {status, uid, name, url} = item;
        if (status === 'done') {
            formValue.push({uid, name, url});
        }
    });
    return formValue;
};

export default (props: FileUploadProps) => {
    const [locale] = useLocale('FileUpload');
    const DEFAULT_TIP_TEXT = locale?.noticeCopywriting;
    const {
        action = DEFAULT_ACTION,
        tipText = DEFAULT_TIP_TEXT,
        value,
        children,
        beforeUpload,
        onChange
    } = props;

    const [fileList, setFileList]: [any[], any] = useState(propToFileList(value));
    const [headers, setHeaders] = useState({});

    // 设置请求头，增加文件上传前处理
    const handleUploadBefore = useCallback((file: any, fileList: any[]) => {
        setHeaders({...headers, 'file-name': encodeURIComponent(file.name)});
        beforeUpload && beforeUpload(file, fileList);
        return true;
    }, [headers, beforeUpload]);

    const handleUploadChange = useCallback((info: any) => {
        const {file, fileList} = info;
        // antd的bug，这里需要深拷贝fileList，不然onChange只会触发一次，相关issue见：
        // https://github.com/ant-design/ant-design/issues/2423
        setFileList(_.cloneDeep(fileList));

        const {status, response} = file;
        // 还有一些其它状态uploading暂不处理
        if (status === 'error') {
            message('error', locale?.uploadFailed);
        }
        else if (status === 'done' || status === 'removed') {
            message('success', locale?.updateCompleted);
            const fileInList = _.find(fileList, fileItem => fileItem.uid === file.uid);
            // 解析response可以加个函数来处理，因为bos和其他后台的response格式并不一样
            if (fileInList) {
                fileInList.url = response.url;
                // todo chenbo09 此处修改了fileList需要重新设置一次，可优化
            }
            // 内部状态的变化通过自动添加的 onChange 方法通知到外部的Form
            if (onChange) {
                const formValue = fileListToForm(fileList);
                onChange(formValue);
            }
        }
    }, [onChange]);

    const uploadProps = _.omit(props, 'action', 'onChange', 'beforeUpload', 'onRemove');
    return (
        <div>
            <Upload
                className={CLS_PREFIX}
                name="file"
                withCredentials
                headers={headers}
                fileList={fileList}
                beforeUpload={handleUploadBefore}
                onChange={handleUploadChange}
                action={action}
                {...uploadProps}
            >
                {children ? children : <Button icon={<OutlinedUpload />}>Upload</Button>}
            </Upload>
            {tipText && <span className={`${CLS_PREFIX}-tip`}>{tipText}</span>}
        </div>
    );
};
